import { KeyRound, Mail } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import authService from "../../services/authService";
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthHeader,
  AuthInput,
  AuthLink,
  AuthStatus,
  PasswordInput,
} from "./AuthPrimitives";
import { isPasswordValid } from "../../utils/passwordPolicy";
import { PasswordRequirements } from "./PasswordRequirements";

type Step = "token" | "password";

export const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token") ?? "";
  const emailFromUrl = searchParams.get("email") ?? "";
  const redirectUri = searchParams.get("redirect_uri");

  const [step, setStep] = useState<Step>(tokenFromUrl ? "password" : "token");
  const [token, setToken] = useState(tokenFromUrl);
  const [userEmail, setUserEmail] = useState(emailFromUrl);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTokenRetry, setShowTokenRetry] = useState(false);
  const [success, setSuccess] = useState(false);

  const resolveEmailFromToken = useCallback(async (resetToken: string) => {
    const trimmedToken = resetToken.trim();
    if (!trimmedToken) return;

    const result = await authService.validateResetToken(trimmedToken);
    if (result?.email) {
      setUserEmail(result.email);
    }
  }, []);

  useEffect(() => {
    if (emailFromUrl) {
      setUserEmail(emailFromUrl);
      return;
    }

    if (tokenFromUrl) {
      void resolveEmailFromToken(tokenFromUrl);
    }
  }, [emailFromUrl, tokenFromUrl, resolveEmailFromToken]);

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToken(e.target.value.trim());
    if (error) setError("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleContinue = async () => {
    const trimmedToken = token.trim();
    if (!trimmedToken) {
      setError(t("Informe o token de redefinição"));
      return;
    }
    setError("");
    setToken(trimmedToken);
    if (!userEmail) {
      await resolveEmailFromToken(trimmedToken);
    }
    setStep("password");
  };

  const validateForm = () => {
    if (!formData.password) {
      setError(t("Senha é obrigatória"));
      return false;
    }
    if (!isPasswordValid(formData.password)) {
      setError(
        t("A senha não atende a todos os requisitos de segurança exigidos."),
      );
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t("Senhas não coincidem"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedToken = token.trim();
    if (!trimmedToken) {
      setError(t("Token de redefinição é obrigatório"));
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setShowTokenRetry(false);

    try {
      const response = await authService.resetPassword(
        trimmedToken,
        formData.password,
      );
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || t("Erro ao redefinir senha"));
      }
    } catch (err: unknown) {
      let errorMessage = t("Erro ao redefinir senha. Tente novamente.");
      let tokenRetry = false;
      const message = err instanceof Error ? err.message : String(err);
      if (
        message.toLowerCase().includes("validation error") ||
        (message.toLowerCase().includes("invalid") &&
          message.toLowerCase().includes("token"))
      ) {
        errorMessage = t(
          "Token inválido ou expirado. Solicite um novo e-mail de redefinição.",
        );
        tokenRetry = true;
      } else if (
        message.toLowerCase().includes("expired") ||
        message.toLowerCase().includes("expir")
      ) {
        errorMessage = t(
          "Token expirado. Solicite um novo e-mail de redefinição.",
        );
        tokenRetry = true;
      } else if (message) {
        errorMessage = message;
      }
      setError(errorMessage);
      setShowTokenRetry(tokenRetry);
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    if (redirectUri) {
      try {
        const url = new URL(redirectUri);
        if (
          url.hostname.endsWith("virtusautomation.com") ||
          url.hostname === "localhost"
        ) {
          window.location.href = redirectUri;
          return;
        }
      } catch (e) {
        console.error("Invalid redirect_uri", e);
      }
    }
    navigate("/auth", {
      state: {
        message: t(
          "Senha redefinida com sucesso! Faça login com sua nova senha.",
        ),
        type: "success",
      },
    });
  };

  if (success) {
    return (
      <div className="space-y-6">
        <AuthStatus title={t("Senha Redefinida com Sucesso!")}>
          <p>
            {t(
              "Sua senha foi redefinida com sucesso. Agora você pode fazer login com sua nova senha.",
            )}
          </p>
        </AuthStatus>
        <AuthButton type="button" onClick={goToLogin}>
          {t("Fazer Login Agora")}
        </AuthButton>
      </div>
    );
  }

  // Etapa 1: informar token
  if (step === "token") {
    return (
      <div className="space-y-5">
        <AuthHeader
          title={t("Redefinir Senha")}
          subtitle={t(
            "Cole o token que você recebeu por e-mail no campo abaixo",
          )}
        />

        <div className="space-y-4">
          <AuthField
            label={t("Token de redefinição")}
            htmlFor="reset-token"
            required
          >
            <AuthInput
              id="reset-token"
              type="text"
              icon={KeyRound}
              value={token}
              onChange={handleTokenChange}
              className="font-mono"
              placeholder={t("Cole aqui o token do e-mail")}
              autoComplete="one-time-code"
              error={!!error}
            />
          </AuthField>

          {error && <AuthAlert variant="error" message={error} />}

          <AuthButton
            type="button"
            onClick={handleContinue}
            disabled={!token.trim()}
          >
            {t("Continuar")}
          </AuthButton>
        </div>

        <p className="text-center text-sm">
          <AuthLink onClick={() => navigate("/auth")}>
            {t("← Voltar ao login")}
          </AuthLink>
        </p>
      </div>
    );
  }

  // Etapa 2: nova senha
  return (
    <div className="space-y-5">
      <AuthHeader
        title={t("Nova Senha")}
        subtitle={t("Digite e confirme sua nova senha")}
      />

      {error && (
        <AuthAlert variant="error" message={error}>
          {showTokenRetry && (
            <AuthLink
              onClick={() => {
                setStep("token");
                setError("");
                setToken("");
                setShowTokenRetry(false);
              }}
              className="text-sm underline"
            >
              {t("Usar outro token")}
            </AuthLink>
          )}
        </AuthAlert>
      )}

      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        {userEmail && (
          <AuthField label={t("Endereço de e-mail")} htmlFor="reset-email">
            <AuthInput
              id="reset-email"
              name="email"
              type="email"
              icon={Mail}
              value={userEmail}
              readOnly
              autoComplete="username"
            />
          </AuthField>
        )}

        <AuthField label={t("Nova Senha")} htmlFor="password" required>
          <PasswordInput
            id="password"
            name="password"
            required
            value={formData.password}
            onChange={handleInputChange}
            autoComplete="new-password"
            placeholder={t("Mínimo 8 caracteres, com letras, número e símbolo")}
          />
        </AuthField>

        <AuthField
          label={t("Confirmar Nova Senha")}
          htmlFor="confirmPassword"
          required
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            required
            value={formData.confirmPassword}
            onChange={handleInputChange}
            autoComplete="new-password"
            placeholder={t("Confirme sua nova senha")}
          />
        </AuthField>

        <PasswordRequirements
          password={formData.password}
          confirmPassword={formData.confirmPassword}
        />

        <AuthButton
          type="submit"
          loading={loading}
          loadingText={t("Redefinindo senha...")}
        >
          {t("Redefinir Senha")}
        </AuthButton>
      </form>

      <p className="text-center text-sm">
        <AuthLink
          onClick={() => {
            setStep("token");
            setError("");
          }}
        >
          {t("← Voltar ao token")}
        </AuthLink>
      </p>
    </div>
  );
};
